import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { ProductCard } from "@/components/ProductCard";
import { KiddeeHero } from "@/components/kiddee/KiddeeHero";
import { KiddeeStory } from "@/components/kiddee/KiddeeStory";
import { KiddeeFaq } from "@/components/kiddee/KiddeeFaq";
import { KiddeeBundles } from "@/components/KiddeeBundles";
import { CollectionJsonLd } from "@/components/kiddee/CollectionJsonLd";
import { PLAY_STYLES } from "@/config/kiddee";
import { KIDDEE_PAGE } from "@/constants/testIds";

const Heading = ({ eyebrow, title, children }) => (
  <div className="max-w-2xl">
    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#4E7B68]">{eyebrow}</p>
    <h2 className="font-heading mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight">{title}</h2>
    {children && <p className="mt-3 text-stone-600">{children}</p>}
  </div>
);

const Grid = ({ items, testId, cols = "lg:grid-cols-4" }) => (
  <div data-testid={testId} className={`mt-8 grid grid-cols-2 sm:grid-cols-3 ${cols} gap-3 sm:gap-6`}>
    {items.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
  </div>
);

export default function KiddeeCollection() {
  const [all, setAll] = useState(null);
  const [style, setStyle] = useState("");
  useSeo({
    title: "Kiddee Approved Dog Toys | Pool, Fetch & Interactive Toys | PetTok Shop",
    description: "Shop dog toys picked by Kiddee, our real rat terrier tester: dog pool toys, fetch toys, squeaky toys, durable chew toys and interactive enrichment toys. Free US shipping over $50.",
    path: "/shop?cat=kiddee",
    image: "/media/kiddee/1.jpg",
  });
  useEffect(() => {
    api.get("/products").then(({ data }) => setAll(data)).catch(() => setAll([]));
  }, []);

  const favorites = useMemo(() => (all || []).filter((p) => p.kiddee_fav), [all]);
  const toys = useMemo(() => (all || []).filter((p) => p.category === "toys"), [all]);
  const styles = PLAY_STYLES.filter((s) => toys.some((p) => p.shop_category === s.id));
  const shown = style ? toys.filter((p) => p.shop_category === style) : toys;
  const listed = useMemo(() => favorites.concat(toys.filter((t) => !t.kiddee_fav)), [favorites, toys]);

  return (
    <main data-testid={KIDDEE_PAGE.page} className="pt-16">
      <CollectionJsonLd products={listed} />
      <KiddeeHero />

      <section id="picks" data-testid={KIDDEE_PAGE.favorites} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20 scroll-mt-20">
        <Heading eyebrow="Kiddee's favorites" title="The picks she keeps choosing">
          Her personal picks of toys, treats and comfort gear, each with the Kiddee approved seal. Tap one for her verdict and the details.
        </Heading>
        {all === null ? <p className="mt-8 text-stone-500">Loading Kiddee's picks…</p> : <Grid items={favorites} testId={KIDDEE_PAGE.favoritesGrid} cols={favorites.length === 5 ? "lg:grid-cols-5" : "lg:grid-cols-4"} />}
      </section>

      <KiddeeBundles />

      {styles.length > 0 && (
        <section data-testid={KIDDEE_PAGE.playStyles} className="bg-[#F6F1E8]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20">
            <Heading eyebrow="Shop by play style" title="How does your dog like to play?" />
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-3">
              {styles.map((s) => {
                const Icon = Icons[s.icon] || Icons.PawPrint;
                const count = toys.filter((p) => p.shop_category === s.id).length;
                const on = style === s.id;
                return (
                  <button
                    key={s.id}
                    data-testid={KIDDEE_PAGE.style(s.id)}
                    aria-pressed={on}
                    onClick={() => { setStyle(on ? "" : s.id); document.getElementById("toys")?.scrollIntoView({ behavior: "smooth" }); }}
                    className={`text-left rounded-[1.5rem] p-5 min-h-[44px] border transition-colors ${on ? "bg-[#1C1917] text-white border-[#1C1917]" : "bg-white border-[#E7E0D3] hover:border-[#E06D44]"}`}
                  >
                    <Icon size={22} className={on ? "text-[#F5A27F]" : "text-[#E06D44]"} />
                    <p className="mt-3 font-heading font-bold">{s.label}</p>
                    <p className={`mt-1 text-sm ${on ? "text-stone-300" : "text-stone-600"}`}>{s.blurb}</p>
                    <p className={`mt-3 text-xs font-semibold ${on ? "text-stone-300" : "text-stone-500"}`}>{count} {count === 1 ? "toy" : "toys"}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section id="toys" data-testid={KIDDEE_PAGE.toys} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20 scroll-mt-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <Heading eyebrow="The toy box" title={style ? PLAY_STYLES.find((s) => s.id === style).label : "All dog toys"} />
          {style && <button data-testid={KIDDEE_PAGE.clearStyle} onClick={() => setStyle("")} className="min-h-[44px] px-5 rounded-full border border-[#E7E0D3] text-sm hover:bg-[#F0EBE1] transition-colors">Show all toys</button>}
        </div>
        {all !== null && <Grid items={shown} testId={KIDDEE_PAGE.toysGrid} />}
      </section>

      <KiddeeStory />
      <KiddeeFaq />

      <section data-testid={KIDDEE_PAGE.finalCta} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20">
        <div className="rounded-[2rem] bg-[#E06D44] text-white p-8 sm:p-12 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Ready for tail-wagging fun?</h2>
            <p className="mt-2 text-white/90">Free US shipping on orders of $50 or more. Happy pets or your money back within 30 days.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href="#picks" data-testid={KIDDEE_PAGE.finalShop} className="min-h-[44px] inline-flex items-center px-6 rounded-full bg-white text-[#1C1917] font-semibold hover:bg-[#FDF2EC] transition-colors">Shop Kiddee's picks</a>
            <Link to="/shop" data-testid={KIDDEE_PAGE.finalAll} className="min-h-[44px] inline-flex items-center px-6 rounded-full border border-white/70 font-semibold hover:bg-white/10 transition-colors">Browse the whole shop</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
